import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LIBRARY_MAX_SLOTS,
  canAddDocument,
  formatSize,
  minutesRemaining,
  sortEntries,
  toEntry,
  type LibraryEntry,
  type LibrarySort,
} from '../app/library-logic.js';
import { useServices } from '../app/services.js';
import { importFile, importPaste } from '../import/import-document.js';
import { createWorkerParse } from '../import/worker-client.js';
import {
  alreadyRequested,
  bannerDismissed,
  canPromptInstall,
  dismissBanner,
  promptInstall,
  requestDurableStorage,
} from '../app/durability.js';
import { Modal } from '../components/Modal.jsx';
import { deleteDocument, getPosition, listDocuments } from '../storage/index.js';

interface ImportState {
  readonly name: string;
  readonly page?: number;
  readonly total?: number;
}

export function LibraryView(): ReactNode {
  const { db, t, settings, locale } = useServices();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LibraryEntry[] | null>(null);
  const [sort, setSort] = useState<LibrarySort>('recent');
  const [importing, setImporting] = useState<ImportState | null>(null);
  const [capModal, setCapModal] = useState(false);
  const [pasteModal, setPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LibraryEntry | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const importAbort = useRef<{ aborted: boolean } | null>(null);
  const [showDurability, setShowDurability] = useState(false);

  const reload = useCallback(async () => {
    const docs = await listDocuments(db);
    const withPositions = await Promise.all(
      docs.map(async (doc) => toEntry(doc, await getPosition(db, doc.id))),
    );
    setEntries(withPositions);
  }, [db]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const file = [...files][0];
      if (file === undefined || entries === null) return;
      // W-1: UX fast-fail only — the admission transaction is the authority.
      if (!canAddDocument(entries.length)) {
        setCapModal(true);
        return;
      }
      setImporting({ name: file.name });
      const signal = { aborted: false };
      importAbort.current = signal;
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        // §6.7 / PD-2: parsing runs in a Worker with progress + cancel.
        const outcome = await importFile(
          db,
          { name: file.name, bytes },
          {
            parse: createWorkerParse(),
            signal,
            onProgress: (page, total) => setImporting({ name: file.name, page, total }),
          },
        );
        // W-1: the commit-boundary rejection — a racing import filled the last
        // slot after our pre-check. Surface the same cap modal, import nothing.
        if (outcome.status === 'library-full') {
          await reload();
          setCapModal(true);
          return;
        }
        if (outcome.status === 'duplicate') showToast(t('import.duplicate'));
        else if (outcome.legacyResumed) showToast(t('library.legacyResume'));
        // PRD §10: request durable storage at FIRST import; never promise.
        if (!(await alreadyRequested(db))) {
          const status = await requestDurableStorage(db);
          if (status !== 'granted' && !(await bannerDismissed(db))) setShowDurability(true);
        }
        await reload();
        void navigate(`/read/${outcome.doc.id}`);
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          showToast(
            `${t('import.failed')}: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
      } finally {
        importAbort.current = null;
        setImporting(null);
      }
    },
    [db, entries, navigate, reload, showToast, t],
  );

  const handlePaste = useCallback(async () => {
    if (entries === null || pasteText.trim().length < 10) return;
    if (!canAddDocument(entries.length)) {
      setPasteModal(false);
      setCapModal(true);
      return;
    }
    setPasteModal(false);
    try {
      // W-1: paste commits through the same admission transaction; the modal's
      // pre-check above is UX polish, not the enforcement.
      const outcome = await importPaste(db, pasteText, t('paste.documentTitle'));
      if (outcome.status === 'library-full') {
        await reload();
        setCapModal(true);
        return;
      }
      setPasteText('');
      if (outcome.status === 'duplicate') showToast(t('import.duplicate'));
      await reload();
      void navigate(`/read/${outcome.doc.id}`);
    } catch (error) {
      showToast(`${t('import.failed')}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [db, entries, navigate, pasteText, reload, showToast, t]);

  const confirmDelete = useCallback(async () => {
    if (deleteTarget === null) return;
    await deleteDocument(db, deleteTarget.doc.id);
    setDeleteTarget(null);
    await reload();
  }, [db, deleteTarget, reload]);

  const sorted = entries === null ? [] : sortEntries(entries, sort);
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  return (
    <div className="app">
      <header className="topbar">
        <Link className="brand" to="/library">
          V<em>i</em>f
        </Link>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={() => setPasteModal(true)}>
            {t('library.paste')}
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => fileInput.current?.click()}>
            {t('library.import')}
          </button>
          <Link className="icon-btn" to="/settings" aria-label={t('settings.title')}>
            ⚙
          </Link>
        </div>
      </header>

      <main className="body">
        <div className="library">
          <div className="library-head">
            <h1>{t('library.title')}</h1>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
              <span className="slot-meter">
                {t('library.slotsUsed', { used: sorted.length, max: LIBRARY_MAX_SLOTS })}
              </span>
              <div className="sort-row">
                {(['recent', 'progress', 'title'] as const).map((key) => (
                  <button
                    key={key}
                    className={`btn btn-sm btn-ghost ${sort === key ? 'active' : ''}`}
                    onClick={() => setSort(key)}
                  >
                    {t(`library.sort.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {sorted.map((entry) => (
            <div
              className="doc-row"
              key={entry.doc.id}
              role="button"
              tabIndex={0}
              onClick={() => void navigate(`/read/${entry.doc.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  void navigate(`/read/${entry.doc.id}`);
                }
              }}
            >
              <span className="badge">{entry.doc.format}</span>
              <div className="doc-main">
                <div className="doc-title">{entry.doc.title}</div>
                <div className="doc-meta">
                  <span>
                    {t('library.progress', { percent: Math.round(entry.progress * 100) })}
                  </span>
                  <span>
                    {t('library.timeLeft', {
                      minutes: minutesRemaining(entry, settings.targetWpm),
                    })}
                  </span>
                  <span>
                    {entry.position === undefined
                      ? t('library.never')
                      : t('library.lastRead', {
                          date: dateFormat.format(entry.position.updatedAt),
                        })}
                  </span>
                </div>
                <div className="doc-progress">
                  <div style={{ width: `${entry.progress * 100}%` }} />
                </div>
              </div>
              <button
                className="btn btn-sm btn-ghost"
                aria-label={t('library.delete')}
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteTarget(entry);
                }}
              >
                ✕
              </button>
            </div>
          ))}

          <div
            className={`dropzone ${dragOver ? 'over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              void handleFiles(event.dataTransfer.files);
            }}
          >
            {entries !== null && entries.length === 0 ? (
              <p style={{ marginBottom: 8 }}>{t('library.empty')}</p>
            ) : null}
            <p>{t('library.dropHint')}</p>
          </div>
        </div>
      </main>

      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.docx,.epub,.txt,.md"
        style={{ display: 'none' }}
        onChange={(event) => {
          if (event.target.files !== null) void handleFiles(event.target.files);
          event.target.value = '';
        }}
      />

      {importing !== null && (
        <div className="toast" role="status">
          {t('import.parsing', { name: importing.name })}
          {importing.page !== undefined && importing.total !== undefined
            ? ` — ${t('import.page', { page: importing.page, total: importing.total })}`
            : ''}
          <button
            className="btn btn-sm btn-ghost"
            style={{ marginLeft: 10 }}
            onClick={() => {
              if (importAbort.current !== null) importAbort.current.aborted = true;
            }}
          >
            {t('import.cancel')}
          </button>
        </div>
      )}
      {toast !== null && <div className="toast">{toast}</div>}

      {showDurability && (
        <div className="banner" role="status">
          <span>{t('durability.banner')}</span>
          <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {canPromptInstall() && (
              <button className="btn btn-sm" onClick={() => void promptInstall()}>
                {t('durability.install')}
              </button>
            )}
            <button
              className="btn btn-sm btn-ghost"
              aria-label={t('common.close')}
              onClick={() => {
                setShowDurability(false);
                void dismissBanner(db);
              }}
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {capModal && (
        <Modal title={t('library.slotCapTitle')} onClose={() => setCapModal(false)}>
          <p style={{ color: 'var(--textMuted)', lineHeight: 1.6 }}>{t('library.slotCap')}</p>
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={() => setCapModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </Modal>
      )}

      {pasteModal && (
        <Modal title={t('paste.title')} onClose={() => setPasteModal(false)}>
          <textarea
            autoFocus
            placeholder={t('paste.placeholder')}
            value={pasteText}
            onChange={(event) => setPasteText(event.target.value)}
          />
          <div className="modal-actions">
            <button className="btn" onClick={() => setPasteModal(false)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              disabled={pasteText.trim().length < 10}
              onClick={() => void handlePaste()}
            >
              {t('paste.start')}
            </button>
          </div>
        </Modal>
      )}

      {deleteTarget !== null && (
        <Modal title={t('library.delete')} onClose={() => setDeleteTarget(null)}>
          <p style={{ color: 'var(--textMuted)', lineHeight: 1.6 }}>
            {t('library.deleteConfirm', {
              title: deleteTarget.doc.title,
              size: formatSize(deleteTarget.doc.size),
            })}
          </p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={() => void confirmDelete()}>
              {t('library.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
